import createSagaMonitor from '@clarketm/saga-monitor';
import {
  type Action,
  configureStore,
  type ConfigureStoreOptions,
  type EnhancedStore,
  type Middleware,
} from '@reduxjs/toolkit';
import ms from 'ms';
import createSagaMiddleware from 'redux-saga';
import { serializeError } from 'serialize-error';
import { assert } from 'ts-essentials';
import { ContainerInstance, Service } from 'typedi';
import { type CcmUserClaims, OktaService } from '../services/okta';
import { SettingsService } from '../services/settings';
import { authSlice } from './auth/auth.slice';
import { errorSlice, type SerializedError } from './error/error.slice';
import { PersistStoreService } from './persistent-store';
import { reducer } from './root.reducer';
import { rootSaga } from './root.saga';
import { type RootState } from './root.state';
import { type ReduxSagaContext } from './types';

export type PrayerStreamStore = EnhancedStore<RootState>;

const DEVTOOLS_INSTANCE_NAME = 'PrayerStream';

export function assertIsAction(action: unknown): asserts action is Action {
  assert(
    typeof action === 'object' &&
      action !== null &&
      'type' in action &&
      typeof action.type === 'string',
  );
}

@Service()
export class StoreService {
  private readonly container: ContainerInstance;

  private readonly persistentStoreService: PersistStoreService;

  private store?: PrayerStreamStore;

  constructor(container: ContainerInstance) {
    this.container = container;
    this.persistentStoreService = container.get(PersistStoreService);
  }

  // TODO: Bring over the improvements from Nextopia.
  async getStore(): Promise<PrayerStreamStore> {
    const { settings } = this.container.get(SettingsService);
    const { oktaAuth } = this.container.get(OktaService);

    if (!this.store) {
      const preloadedState =
        (await this.persistentStoreService.loadState()) ?? {};
      const sagaMiddleware = createSagaMiddleware<ReduxSagaContext>({
        context: {
          container: this.container,
        },
        onError: (error, { sagaStack }) => {
          const { store } = this;
          if (store) {
            const fatalError: SerializedError = serializeError(error);
            fatalError.sagaStack = sagaStack;
            store.dispatch(errorSlice.actions.setFatalError(fatalError));
          }
        },
        sagaMonitor: settings.enableReduxSagaLogging
          ? createSagaMonitor({
              actionDispatch: true,
              effectCancel: true,
              effectReject: true,
              effectResolve: true,
              effectTrigger: true,
              level: 'log',
              rootSagaStart: true,
            })
          : undefined,
      });

      const getMiddleware: Required<
        ConfigureStoreOptions<RootState>
      >['middleware'] = (getDefaultMiddleware) => {
        const testOptions = settings.enableReduxChecks
          ? {
              immutableCheck: { warnAfter: ms('1s') },
              serializableCheck: { warnAfter: ms('1s') },
            }
          : {};

        return (
          getDefaultMiddleware({
            thunk: false,
            ...testOptions,
          })
            .prepend(this.persistMiddleware)
            // eslint-disable-next-line unicorn/prefer-spread -- Cannot use the spread operator because a Tuple is required.
            .concat(sagaMiddleware)
        );
      };

      const store = configureStore({
        devTools: settings.enableReduxDevTools
          ? { name: DEVTOOLS_INSTANCE_NAME }
          : false,
        middleware: getMiddleware,
        preloadedState,
        reducer,
      });

      oktaAuth.authStateManager.subscribe(({ idToken, isAuthenticated }) => {
        try {
          if (isAuthenticated) {
            assert(idToken, 'idToken');
            const { contactUID, name, preferred_username } =
              idToken.claims as CcmUserClaims;

            assert(contactUID);
            assert(name);
            assert(preferred_username);

            store.dispatch(
              authSlice.actions.setLoggedIn({
                contactUid: contactUID,
                name,
                username: preferred_username,
              }),
            );
            store.dispatch(authSlice.actions.loginSucceeded());
          } else {
            store.dispatch(authSlice.actions.setLoggedOut());
            store.dispatch(authSlice.actions.logoutSucceeded());
          }
        } catch (error) {
          if (this.store) {
            const fatalError = serializeError(error as Error);
            store.dispatch(errorSlice.actions.setFatalError(fatalError));
          }
        }
      });

      if (oktaAuth.token.isLoginRedirect()) {
        try {
          await oktaAuth.handleLoginRedirect();
        } catch {
          // TODO: Log the error and do nothing. This case is handled gracefully by
          // the router.
        }
      } else if (await oktaAuth.session.exists()) {
        try {
          const { tokens } = await oktaAuth.token.getWithoutPrompt({
            maxAge: settings.loginMaxAgeSeconds,
          });
          oktaAuth.tokenManager.setTokens(tokens);
        } catch {
          await oktaAuth.signInWithRedirect({
            maxAge: settings.loginMaxAgeSeconds,
          });
        }
      }

      this.store = store;
      await oktaAuth.start();
      sagaMiddleware.run(rootSaga);
    }

    return this.store;
  }

  private readonly persistMiddleware: Middleware<
    Record<string, unknown>,
    RootState
  > = (store) => {
    const filterState = ({ auth }: RootState): Partial<RootState> => {
      return { auth };
    };

    const authActions = authSlice.actions;

    return (next) => {
      return (action) => {
        const result = next(action);

        assertIsAction(action);

        switch (action.type) {
          // TODO: Add tests to catch when an action should be listed here.
          case authActions.clearContinueRoutePostLogin.type:
          case authActions.clearContinueRoutePostLogout.type:
          case authActions.setContinueRoutePostLogin.type:
          case authActions.setContinueRoutePostLogout.type:
          case authActions.setLoggedIn.type:
          case authActions.setLoggedOut.type:
            void this.persistentStoreService.saveState(
              filterState(store.getState()),
            );
            break;
          default:
            break;
        }

        return result;
      };
    };
  };
}

export type PrayerStreamStoreOptions = {
  container: ContainerInstance;
};

export const createPrayerStreamStore = async ({
  container,
}: PrayerStreamStoreOptions): Promise<PrayerStreamStore> => {
  // const { environment } = container.get(EnvironmentService<AppEnvironment>);
  // const logger = container.get(CcmLogger);

  const storeService = container.get(StoreService);
  return storeService.getStore();
};
